import type { AdminLedger, BootstrapData, Contribution, MemberSummary, ReportPeriod, ReportResult } from "./shared/types";

export const demoBootstrap: BootstrapData = {
  currentMemberId: "cg_001",
  congregants: [
    { id: "cg_001", memberCode: "1001", name: "Mary Wanjiku", phone: "+254700111001", email: "mary@example.church" },
    { id: "cg_002", memberCode: "1002", name: "John Mwangi", phone: "+254700111002", email: "john@example.church" },
    { id: "cg_003", memberCode: "1003", name: "Faith Achieng", phone: "+254700111003", email: "faith@example.church" }
  ],
  purposes: [
    { code: "tithe", label: "Tithe", active: true },
    { code: "offering", label: "Offering", active: true },
    { code: "building", label: "Building Fund", active: true },
    { code: "missions", label: "Missions", active: true },
    { code: "welfare", label: "Welfare", active: true },
    { code: "general", label: "General", active: true },
    { code: "unclassified", label: "Unclassified", active: true }
  ]
};

export const demoContributions: Contribution[] = [
  {
    id: "ct_001",
    congregantId: "cg_001",
    congregantName: "Mary Wanjiku",
    memberCode: "1001",
    amount: "12000.00",
    currency: "KES",
    purpose: "tithe",
    method: "paybill",
    status: "classified",
    providerTransactionId: "SEED001",
    providerAccount: "TITHE-1001",
    paidAt: "2026-07-04T08:12:00.000Z",
    receivedAt: "2026-07-04T08:12:08.000Z",
    note: "Paybill callback"
  },
  {
    id: "ct_002",
    congregantId: "cg_002",
    congregantName: "John Mwangi",
    memberCode: "1002",
    amount: "3500.00",
    currency: "KES",
    purpose: "offering",
    method: "paybill",
    status: "classified",
    providerTransactionId: "SEED002",
    providerAccount: "OFFERING-1002",
    paidAt: "2026-07-04T09:28:00.000Z",
    receivedAt: "2026-07-04T09:28:04.000Z"
  },
  {
    id: "ct_003",
    congregantId: "cg_003",
    congregantName: "Faith Achieng",
    memberCode: "1003",
    amount: "5000.00",
    currency: "KES",
    purpose: "building",
    method: "paybill",
    status: "classified",
    providerTransactionId: "SEED003",
    providerAccount: "BUILD-1003",
    paidAt: "2026-07-03T17:02:00.000Z",
    receivedAt: "2026-07-03T17:02:06.000Z"
  },
  {
    id: "ct_004",
    congregantId: null,
    congregantName: "Unmatched contributor",
    memberCode: null,
    amount: "2200.00",
    currency: "KES",
    purpose: "unclassified",
    method: "till",
    status: "unclassified",
    providerTransactionId: "SEED004",
    providerAccount: null,
    paidAt: "2026-07-03T18:11:00.000Z",
    receivedAt: "2026-07-03T18:11:09.000Z",
    note: "Till payment awaiting reconciliation"
  }
];

export async function fetchBootstrap(): Promise<BootstrapData> {
  return fetchJson("/api/bootstrap", demoBootstrap);
}

export async function fetchPublicSummary(): Promise<AdminLedger> {
  return fetchJson("/api/public/summary", makeDemoPublicLedger());
}

export async function fetchMemberSummary(memberId: string): Promise<MemberSummary> {
  return fetchJson(`/api/member/${encodeURIComponent(memberId)}/summary`, makeDemoMemberSummary(memberId));
}

export async function lookupMemberSummary(phone: string, receiptCode: string): Promise<MemberSummary> {
  try {
    const response = await fetch("/api/member/lookup", {
      method: "POST",
      headers: { accept: "application/json", "content-type": "application/json" },
      body: JSON.stringify({ phone, receiptCode })
    });
    if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) {
      const body = response.headers.get("content-type")?.includes("application/json") ? ((await response.json()) as { error?: string }) : {};
      throw new Error(body.error ?? "No matching contribution record.");
    }
    return (await response.json()) as MemberSummary;
  } catch {
    throw new Error("No matching contribution record.");
  }
}

export async function fetchAdminLedger(): Promise<AdminLedger> {
  return fetchJson("/api/admin/ledger", makeDemoAdminLedger());
}

export async function fetchReport(period: ReportPeriod, congregantId?: string): Promise<ReportResult> {
  const params = new URLSearchParams({ period });
  if (congregantId) params.set("congregantId", congregantId);
  return fetchJson(`/api/reports?${params.toString()}`, makeDemoReport(period, congregantId));
}

async function fetchJson<T>(path: string, fallback: T): Promise<T> {
  try {
    const response = await fetch(path, { headers: { accept: "application/json" } });
    if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

function makeDemoMemberSummary(memberId: string): MemberSummary {
  const congregant = demoBootstrap.congregants.find((item) => item.id === memberId) ?? demoBootstrap.congregants[0]!;
  const contributions = demoContributions.filter((item) => item.congregantId === congregant.id);
  return {
    congregant,
    totals: { lifetime: "12000.00", monthToDate: "12000.00", yearToDate: "12000.00", today: "12000.00" },
    byPurpose: [{ purpose: "tithe", amount: "12000.00", count: 1 }],
    contributions,
    generatedAt: new Date().toISOString()
  };
}

function makeDemoAdminLedger(): AdminLedger {
  return {
    totals: { lifetime: "22700.00", monthToDate: "22700.00", yearToDate: "22700.00", today: "15500.00" },
    byPurpose: [
      { purpose: "tithe", amount: "12000.00", count: 1 },
      { purpose: "building", amount: "5000.00", count: 1 },
      { purpose: "offering", amount: "3500.00", count: 1 },
      { purpose: "unclassified", amount: "2200.00", count: 1 }
    ],
    contributions: demoContributions,
    unclassifiedCount: 1,
    generatedAt: new Date().toISOString()
  };
}

function makeDemoPublicLedger(): AdminLedger {
  const ledger = makeDemoAdminLedger();
  return {
    ...ledger,
    contributions: ledger.contributions.map((item) => ({
      ...item,
      congregantName: item.congregantName === "Unmatched contributor" ? item.congregantName : `${item.congregantName.split(" ")[0]} ${item.congregantName.split(" ")[1]?.[0] ?? ""}.`
    }))
  };
}

function makeDemoReport(period: ReportPeriod, congregantId?: string): ReportResult {
  const contributions = congregantId ? demoContributions.filter((item) => item.congregantId === congregantId) : demoContributions;
  return {
    period,
    title: `${period[0]?.toUpperCase()}${period.slice(1)} contribution report`,
    rows: contributions.map((item) => ({ label: item.congregantName, count: 1, amount: item.amount })),
    total: contributions.length === 1 ? contributions[0]!.amount : "22700.00",
    generatedAt: new Date().toISOString()
  };
}
